<template>
  <div class="global-container">
    <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
      <template v-slot:header-options>
        <button @click="exportToExcel" class="action-button ghost only-desktop-flex" type="button" title="Exporter les factures affichées (Excel)"><span class="fas fa-download"></span>Exporter</button>
      </template>
      <template v-slot:table>
        <thead>
        <tr>
          <custom-header class="col-fit" field="invoiceNumber">N°</custom-header>
          <custom-header field="title" class="hide-for-mobile col-fill">Titre</custom-header>
          <custom-header field="status" class="hide-for-mobile col-fit">Status</custom-header>
          <custom-header class="col-fit" field="clientName">Client</custom-header>
          <custom-header field="ttcAmount" class="hide-for-mobile col-fit">Montant TTC</custom-header>
          <custom-header field="htAmount" class="hide-for-mobile col-fit">Montant HT</custom-header>
          <custom-header class="col-fit" field="tvaAmount">TVA</custom-header>
          <custom-header class="col-fit" field="tvaRemaining">TVA non encaissée</custom-header>
          <custom-header field="remaining" class="hide-for-mobile col-fit">Solde</custom-header>
        </tr>
        </thead>
        <tbody v-if="data && data.length > 0">
        <tr v-if="isResumeLoading" class="total compact-row">
          <td colspan="9" class="resume-loading"><CustomLoader ref="resumeLoader" /></td>
        </tr>
        <tr v-else-if="resumeData" class="total compact-row">
          <td>Total ({{ resumeData.count }})</td>
          <td>-</td>
          <td>-</td>
          <td>{{ resumeData.clientCount }}</td>
          <td>{{ money(resumeData.ttcTotal) }}</td>
          <td>{{ money(resumeData.htTotal) }}</td>
          <td>{{ money(resumeData.tvaTotal) }}</td>
          <td>{{ money(resumeData.tvaTotalUncollected) }}</td>
          <td>{{ money(resumeData.remainingTotal) }}</td>
        </tr>
        <tr v-for="(business, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" :class="isAccountantModule ? 'compact-row' : 'business-row'" @click="openBusiness(business)">
          <custom-body class="col-fit" field="invoiceNumber">
            <div class="document-numbers">
              <template v-for="doc in getDocuments(business)" :key="doc.key">
                <DocumentChips v-if="doc.type === 'invoice'" :invoice-number="doc.number" />
                <DocumentChips v-else-if="doc.type === 'deposit'" :deposit-number="doc.number" />
                <DocumentChips v-else-if="doc.type === 'credit-note'" :credit-note-numbers="[doc.number]" />
              </template>
            </div>
          </custom-body>
          <custom-body field="title" class="hide-for-mobile col-fill" :title="business.title">{{ business.title ? business.title : ''}}</custom-body>
          <custom-body field="status" class="hide-for-mobile col-fit"><StatusBadge :status="business.status" /></custom-body>
          <custom-body class="col-fit cell-strong col-truncate" field="clientName" :title="business.clientName || ''">{{ business.clientName || '' }}</custom-body>
          <custom-body field="ttcAmount" class="hide-for-mobile col-fit cell-strong">{{ business.ttcAmount ? money(business.ttcAmount) : '' }}</custom-body>
          <custom-body field="htAmount" class="hide-for-mobile col-fit">{{ business.htAmount ? money(business.htAmount) : '' }}</custom-body>
          <custom-body class="col-fit" field="tvaAmount">{{ business.tvaAmount ? money(business.tvaAmount) : '' }}</custom-body>
          <custom-body class="col-fit" field="tvaRemaining">{{ business.tvaRemaining ? money(business.tvaRemaining) : '' }}</custom-body>
          <custom-body field="remaining" class="hide-for-mobile col-fit">{{ business.remaining ? money(business.remaining) : '' }}</custom-body>
        </tr>
        </tbody>
        <tbody v-else><tr class="empty-row"><td colspan="9">Aucun contrat enregistré</td></tr></tbody>
      </template>
    </CustomTable>
    <BusinessManagementModal ref="businessManagementModal" read-only />
  </div>
</template>

<script>
import {BusinessController} from "@/controllers/business/BusinessController";
import {AccountingController} from "@/accounting/controllers/AccountingController";
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import CustomTable from "@/components/CustomTable.vue";
import CustomLoader from "@/components/CustomLoader.vue";
import { library } from '@fortawesome/fontawesome-svg-core';
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import BusinessManagementModal from "@/components/modals/BusinessManagementModal.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import DocumentChips from "@/components/tables/commons/DocumentChips.vue";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {removePaginationFromURLParams} from "@/utils";
import {AccountingService} from "@/accounting/services/AccountingService";
import {dateToFec, displayDate, documentRef, excelDate, money} from "@/accounting/services/export/AccountingFormat";
import {DATE_FORMAT, MONEY_FORMAT, downloadWorkbook, singleTableWorkbook} from "@/accounting/services/export/ExcelTable";
library.add(faDownload);

export default {
  name: 'InvoicesTable',
  computed: {
    isAccountantModule(){
      return localStorage && localStorage.options && localStorage.options.includes('accountant');
    },
    periodData() {
      return AccountingService.data;
    },
    // Comptable : le professionnel sélectionné (undefined pour un professionnel sur ses propres données)
    professionalId() {
      return this.isAccountantModule ? AccountingService.data.selectedProfessional?.id : undefined;
    }
  },
  components: {
    BusinessManagementModal,
    StatusBadge,
    DocumentChips,
    CustomBody,
    CustomHeader,
    CustomTable,
    CustomLoader
  },
  data: function () {
    return {
      data: [],
      resumeData: undefined,
      isResumeLoading: false,
      elements: [
        {label: "Date de création", value: "invoiceCreationDate", type: "date"},
        {label: "N°", value: "invoiceNumber", type: "number"},
        {label: "Titre", value: "title", type: "text"},
        {label: "Status", value: "status", type: "select", values: BUSINESS_STATUS_ENUM},
        {label: "Client", value: "clientName", type: "text"},
        {label: "Montant TTC", value: "ttcAmount", type: "number"},
        {label: "Montant HT", value: "htAmount", type: "number"},
        {label: "TVA", value: "tvaAmount", type: "number"},
        {label: "TVA non encaissée", value: "tvaRemaining", type: "number"},
        {label: "Solde", value: "remaining", type: "number"},
      ],
      searchOptions: {
        filters: AccountingService.periodFilters("invoiceCreationDate"),
        sorts: [
          {field: "invoiceNumber", order: SORT_OPERATOR_ENUM.DESCENDING.value},
        ]
      },
    }
  },
  watch: {
    async "periodData.selectedPeriod"(val) {
      AccountingService.updatePeriodFilters(this.searchOptions.filters, "invoiceCreationDate", val);

      this.$refs.table?.startLoading();
      await this.loadData();
      this.$refs.table?.stopLoading();
      await this.loadResumeData();
    }
  },
  async mounted() {
    this.$refs.table?.startLoading();
    this.$refs.table.$refs.searchArray.initWithURLParams();
    await this.loadData();
    this.$refs.table?.stopLoading();
    await this.loadResumeData();
  },
  methods: {
    money,
    getDocuments(business) {
      const documents = [];
      if (Number(business.invoiceNumber) > 0) documents.push({key: 'invoice', type: 'invoice', number: business.invoiceNumber});
      if (Number(business.depositNumber) > 0) documents.push({key: 'deposit', type: 'deposit', number: business.depositNumber});
      (business.creditNotes || []).forEach((creditNote, index) => documents.push({key: 'credit-note-' + index, type: 'credit-note', number: creditNote.number}));
      return documents;
    },
    async openBusiness(business) {
      if (!business.id) return;
      const fullBusiness = await BusinessController.get(business.id);
      if (fullBusiness && !fullBusiness.errors) {
        this.$refs.businessManagementModal.openModal(fullBusiness);
      }
    },
    // Factures correspondant à la recherche (période + filtres), avec ou sans la pagination du tableau
    async fetchInvoices(params) {
      // Comptable : aucun client (professionnel) sélectionné, rien à charger
      if (this.isAccountantModule && !this.professionalId) return [];
      const resp = await AccountingController.getInvoiceTable(params, this.professionalId);
      return resp && !resp.errors ? resp : [];
    },
    async loadData() {
      if (!this.$refs.table) return;
      this.data = [];
      this.data = await this.fetchInvoices(this.$refs.table.buildURLParams());
    },
    async loadResumeData() {
      if (this.isAccountantModule && !this.professionalId) return;
      // Si le premier load n'a pas finit et que l'on change d'onglet ça casse la ref
      if (!this.$refs.table) return;
      this.resumeData = undefined;
      this.isResumeLoading = true;
      this.$nextTick(() => this.$refs.resumeLoader?.startLoading());
      const resp = await AccountingController.getInvoiceResume(removePaginationFromURLParams(this.$refs.table.buildURLParams()), this.professionalId);
      if(resp && !resp.errors) {
        this.resumeData = resp;
      }
      this.isResumeLoading = false;
    },
    // Export de toute la recherche (période + filtres), sans la pagination du tableau, par numéro de facture
    async exportToExcel() {
      const invoices = await this.fetchInvoices(removePaginationFromURLParams(this.$refs.table.buildURLParams()));
      invoices.sort((a, b) => a.invoiceNumber - b.invoiceNumber);
      const workbook = singleTableWorkbook('Factures', [
        {header: 'Facture', pick: row => documentRef('FA', row.invoiceNumber)},
        {header: 'Date', pick: row => excelDate(dateToFec(row.invoiceCreationDate)), format: DATE_FORMAT},
        {header: 'Titre', pick: row => row.title},
        {header: 'Statut', pick: row => BUSINESS_STATUS_ENUM[row.status]?.label ?? row.status},
        {header: 'Client', pick: row => row.clientName},
        {header: 'Montant HT', pick: row => row.htAmount, format: MONEY_FORMAT, total: true},
        {header: 'TVA', pick: row => row.tvaAmount, format: MONEY_FORMAT, total: true},
        {header: 'Montant TTC', pick: row => row.ttcAmount, format: MONEY_FORMAT, total: true},
        {header: 'TVA non encaissée', pick: row => row.tvaRemaining, format: MONEY_FORMAT, total: true},
        {header: 'Solde', pick: row => row.remaining, format: MONEY_FORMAT, total: true},
      ], invoices);
      const {StartPeriodDate, EndPeriodDate} = this.periodData.selectedPeriod;
      downloadWorkbook(workbook, `Factures du ${displayDate(dateToFec(StartPeriodDate)).replace(/\//g, '-')} au ${displayDate(dateToFec(EndPeriodDate)).replace(/\//g, '-')}.xlsx`);
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';

.global-container
  display flex
  flex-direction column

  .text-container
    display flex

.document-numbers
  display grid
  grid-template-columns repeat(2, max-content)
  align-items center
  gap 4px 6px

.total
  background-color #b4bbd9

  &:hover
    background-color #cbcfdd !important

  td
    font-weight bold

// CustomLoader est en position absolute par défaut : dans la cellule de résumé on le remet dans le flux
// pour que le td prenne la hauteur du loader (la classe du root de CustomLoader reçoit aussi le scope de ce composant)
.resume-loading
  padding 0

  .loader
    position static
    height 48px
    border-radius 0
    box-shadow none
</style>